import type { DelegationResponse, ValidatorInfo } from '@/types/staking';
import { ValidatorStatus } from '@/types/staking';
import {
  EXCLUDED_TOP_VALIDATORS_COUNT,
  EXCLUDED_VALIDATOR_DUST_THRESHOLD_GRAV,
} from '@/config/constants';

/**
 * Represents a delegation with its validator info for redelegation calculations
 */
export interface DelegationWithValidator {
  validator_address: string;
  validator_moniker?: string;
  amount: bigint; // in micro units (ugraviton)
  is_active: boolean;
}

/**
 * Represents a single redelegation operation
 */
export interface RedelegationOperation {
  from_validator_address: string;
  to_validator_address: string;
  amount: string; // in micro units as string for transaction
}

/**
 * Result of calculating redelegation operations
 */
export interface RedelegationPlan {
  operations: RedelegationOperation[];
  total_amount_redelegated: bigint;
  source_validators: string[];
  target_validators: string[];
}

/**
 * Build the set of operator addresses for the top N validators by voting power.
 * Only bonded (status === 3) validators are considered.
 * This is the single source of truth for the exclusion list — call it once per
 * render and pass the resulting Set to `isExcludedTopValidator`.
 */
export function getExcludedTopValidatorAddresses(
  validators: ValidatorInfo[],
  count: number = EXCLUDED_TOP_VALIDATORS_COUNT,
): Set<string> {
  return new Set(
    [...validators]
      .filter((v) => v.status === 3 && !v.jailed)
      .sort((a, b) => parseFloat(b.tokens) - parseFloat(a.tokens))
      .slice(0, count)
      .map((v) => v.operator_address),
  );
}

/**
 * Check whether a delegation to an excluded-top-validator is large enough to
 * be considered a real position (not dust).  Delegations at or above the
 * threshold are "significant" and trigger the exclusion restrictions.
 *
 * @param delegationAmountMicro  The delegation balance in micro-units (ugraviton)
 * @returns true when the delegation is >= EXCLUDED_VALIDATOR_DUST_THRESHOLD_GRAV
 */
export function isSignificantExcludedDelegation(delegationAmountMicro: string): boolean {
  const amountGrav = parseFloat(delegationAmountMicro) / 1e6;
  return amountGrav >= EXCLUDED_VALIDATOR_DUST_THRESHOLD_GRAV;
}

/**
 * Check if a validator is considered "active" (bonded and not jailed)
 * Validators that are unbonding or unbonded are considered inactive
 */
export function isValidatorActive(validator: ValidatorInfo): boolean {
  // Status: BONDED = 3 is the only active state
  // UNBONDING = 2 is transitioning out, UNBONDED = 1 is inactive
  return validator.status === Number(ValidatorStatus.BONDED) && !validator.jailed;
}

/**
 * Check if a validator is in a problematic state that requires redelegation
 * This includes unbonded, unbonding, or jailed validators
 */
export function isValidatorInactive(validator: ValidatorInfo): boolean {
  return !isValidatorActive(validator);
}

/**
 * Parse delegations and categorize them by validator activity status
 */
export function categorizeDelegations(
  delegations: DelegationResponse[],
  validatorMap: Map<string, ValidatorInfo>
): {
  activeDelegations: DelegationWithValidator[];
  inactiveDelegations: DelegationWithValidator[];
} {
  const activeDelegations: DelegationWithValidator[] = [];
  const inactiveDelegations: DelegationWithValidator[] = [];

  for (const delegation of delegations) {
    const validatorAddress = delegation.delegation.validator_address;
    const validator = validatorMap.get(validatorAddress);
    const amount = BigInt(delegation.balance.amount);

    const delegationWithValidator: DelegationWithValidator = {
      validator_address: validatorAddress,
      validator_moniker: validator?.description?.moniker,
      amount,
      is_active: validator !== undefined && isValidatorActive(validator),
    };

    if (delegationWithValidator.is_active) {
      activeDelegations.push(delegationWithValidator);
    } else {
      inactiveDelegations.push(delegationWithValidator);
    }
  }

  console.log(
    '[redelegation-debug] categorizeDelegations: totalDelegations=', delegations.length,
    'activeCount=', activeDelegations.length,
    'inactiveCount=', inactiveDelegations.length,
  );

  return { activeDelegations, inactiveDelegations };
}

/**
 * Check if the "Redelegate from inactive validators" option should be shown
 * 
 * Requirements:
 * - User has at least one delegation to an inactive validator
 * - User has at least one delegation to an active validator (to redelegate to)
 */
export function shouldShowRedelegateFromInactive(
  delegations: DelegationResponse[],
  validatorMap: Map<string, ValidatorInfo>
): boolean {
  const { activeDelegations, inactiveDelegations } = categorizeDelegations(
    delegations,
    validatorMap
  );

  return inactiveDelegations.length > 0 && activeDelegations.length > 0;
}

/**
 * Calculate redelegation operations to redistribute stake from inactive validators
 * evenly across active validators, using as few redelegate messages as possible.
 *
 * This works in two steps:
 * 1. Compute the *final desired* top-up amount for each active validator, as if all
 *    inactive stake were pooled together and split evenly (base amount + remainder
 *    distributed one unit at a time to the first validators, so no dust is left).
 * 2. Pull the amounts required for each target out of the pool of inactive ("source")
 *    validators greedily (like the classic transportation-problem "northwest corner"
 *    method): fill the current target from the current source until either the
 *    source is exhausted (move to the next source) or the target is satisfied (move
 *    to the next target).
 *
 * Because step 2 never needs to revisit a fully-drained source or a fully-satisfied
 * target, the number of operations is at most `sources.length + targets.length - 1`,
 * instead of `sources.length * targets.length` for the naive "split every source
 * across every target" approach. The final per-validator amounts (and therefore the
 * end state of everyone's stake) are identical either way.
 *
 * Example: If user has delegations A(300), B(300 inactive), C(300), D(300)
 * and B is inactive, this will create operations to move:
 * - 100 from B to A
 * - 100 from B to C
 * - 100 from B to D
 *
 * @param delegations - The user's current delegations
 * @param validatorMap - Map of validator addresses to their info
 * @returns RedelegationPlan with all operations needed
 */
export function calculateRedelegationFromInactive(
  delegations: DelegationResponse[],
  validatorMap: Map<string, ValidatorInfo>
): RedelegationPlan {
  const { activeDelegations, inactiveDelegations } = categorizeDelegations(
    delegations,
    validatorMap
  );

  // If no active validators to redelegate to, or no inactive validators, return empty plan
  if (activeDelegations.length === 0 || inactiveDelegations.length === 0) {
    return {
      operations: [],
      total_amount_redelegated: BigInt(0),
      source_validators: [],
      target_validators: [],
    };
  }

  // The pool of inactive validators we are pulling stake from. Skip zero-amount
  // delegations up front since they contribute nothing to redistribute.
  const sources = inactiveDelegations
    .filter((d) => d.amount > BigInt(0))
    .map((d) => ({ address: d.validator_address, remaining: d.amount }));

  const totalAmountRedelegated = sources.reduce((sum, s) => sum + s.remaining, BigInt(0));

  console.log(
    '[redelegation-debug] calculateRedelegationFromInactive: activeDelegations.length=', activeDelegations.length,
    'inactiveDelegations.length=', inactiveDelegations.length,
    'sources.length (non-zero inactive)=', sources.length,
    'totalAmountRedelegated=', totalAmountRedelegated.toString(),
    'naive max operations (inactive * active)=', inactiveDelegations.length * activeDelegations.length,
    'greedy max operations (sources + targets - 1)=', sources.length + activeDelegations.length - 1,
  );

  if (totalAmountRedelegated <= BigInt(0)) {
    return {
      operations: [],
      total_amount_redelegated: BigInt(0),
      source_validators: [],
      target_validators: [],
    };
  }

  // Step 1: compute the final desired top-up amount for each active validator, as if
  // the entire pool of inactive stake were split evenly across all active validators.
  const numTargets = BigInt(activeDelegations.length);
  const baseAmount = totalAmountRedelegated / numTargets;
  const remainder = totalAmountRedelegated % numTargets;

  const targets = activeDelegations
    .map((d, i) => {
      // Add 1 extra unit to the first 'remainder' validators to handle dust.
      const extraUnit = BigInt(i) < remainder ? BigInt(1) : BigInt(0);
      return { address: d.validator_address, remaining: baseAmount + extraUnit };
    })
    .filter((t) => t.remaining > BigInt(0));

  // Step 2: pull the amounts required for each target from the pool of source
  // (inactive) validators greedily, minimizing the number of operations needed.
  const operations: RedelegationOperation[] = [];
  let i = 0;
  let j = 0;
  while (i < sources.length && j < targets.length) {
    const source = sources[i];
    const target = targets[j];
    if (source === undefined || target === undefined) break;

    const amount = source.remaining < target.remaining ? source.remaining : target.remaining;
    if (amount > BigInt(0)) {
      operations.push({
        from_validator_address: source.address,
        to_validator_address: target.address,
        amount: amount.toString(),
      });
    }

    source.remaining -= amount;
    target.remaining -= amount;
    if (source.remaining <= BigInt(0)) i++;
    if (target.remaining <= BigInt(0)) j++;
  }

  console.log(
    '[redelegation-debug] final plan: operations.length=', operations.length,
    'total_amount_redelegated=', totalAmountRedelegated.toString(),
    'source_validators.length=', sources.length,
    'target_validators.length=', activeDelegations.length,
  );

  return {
    operations,
    total_amount_redelegated: totalAmountRedelegated,
    source_validators: [...new Set(sources.map((s) => s.address))], // deduplicate
    target_validators: [...new Set(activeDelegations.map((d) => d.validator_address))], // deduplicate
  };
}

/**
 * Get a human-readable summary of the redelegation plan
 */
export function getRedelegationSummary(
  plan: RedelegationPlan,
  validatorMap: Map<string, ValidatorInfo>
): string {
  if (plan.operations.length === 0) {
    return 'No redelegation needed.';
  }

  const getValidatorName = (address: string): string => {
    const validator = validatorMap.get(address);
    return validator?.description?.moniker ?? address.slice(0, 20) + '...';
  };

  const sourceNames = plan.source_validators.map(getValidatorName);
  const targetNames = plan.target_validators.map(getValidatorName);
  
  // Convert to GRAV for display (divide by 1e6)
  const totalGrav = Number(plan.total_amount_redelegated) / 1e6;

  return `Redelegate ${totalGrav.toFixed(2)} GRAV from ${sourceNames.join(', ')} to ${targetNames.join(', ')} in ${plan.operations.length} operation${plan.operations.length !== 1 ? 's' : ''}.`;
}
